import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { APIHOST } from '../../common';
import { Conference } from '../../types/conference';
import { ConferenceHotel } from '../../types/conferenceHotel';
import { Hotel } from '../../types/hotel';

interface EditConferenceHotelDialogProps {
  open: boolean;
  onClose: () => void;
  conference: Conference;
  conferenceHotel?: ConferenceHotel | null;
  onUpdate: () => void;
}

export default function EditConferenceHotelDialog({ 
  open, 
  onClose, 
  conference, 
  conferenceHotel, 
  onUpdate 
}: EditConferenceHotelDialogProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [availableHotels, setAvailableHotels] = useState<Hotel[]>([]);
  const [formData, setFormData] = useState({
    hotel: '',
    contactEmail: '',
    contactName: '',
    contactPhone: '',
    dateToSendCSVFile: '',
    earliestAvailable: '',
    latestAvailable: '',
    hasBusParking: false,
    hasTransitToVenue: false,
    requiresCreditCard: false,
    taxPerRoomNight: '',
    taxPerRoomNightNonProfit: '',
    taxRatePercentage: '',
    taxRatePercentageNonProfit: '',
    tier: 'A',
    invoiceNotes: ''
  });

  const isEditMode = Boolean(conferenceHotel);

  useEffect(() => {
    if (open) {
      if (isEditMode && conferenceHotel) {
        // Populate form with existing data
        setFormData({
          hotel: conferenceHotel.hotel?.documentId || '',
          contactEmail: conferenceHotel.contactEmail || '',
          contactName: conferenceHotel.contactName || '',
          contactPhone: conferenceHotel.contactPhone || '',
          dateToSendCSVFile: conferenceHotel.dateToSendCSVFile ? 
            new Date(conferenceHotel.dateToSendCSVFile).toISOString().split('T')[0] : '',
          earliestAvailable: conferenceHotel.earliestAvailable ? 
            new Date(conferenceHotel.earliestAvailable).toISOString().split('T')[0] : '',
          latestAvailable: conferenceHotel.latestAvailable ? 
            new Date(conferenceHotel.latestAvailable).toISOString().split('T')[0] : '',
          hasBusParking: conferenceHotel.hasBusParking || false,
          hasTransitToVenue: conferenceHotel.hasTransitToVenue || false,
          requiresCreditCard: conferenceHotel.requiresCreditCard || false,
          taxPerRoomNight: conferenceHotel.taxPerRoomNight?.toString() || '',
          taxPerRoomNightNonProfit: conferenceHotel.taxPerRoomNightNonProfit?.toString() || '',
          taxRatePercentage: conferenceHotel.taxRatePercentage?.toString() || '',
          taxRatePercentageNonProfit: conferenceHotel.taxRatePercentageNonProfit?.toString() || '',
          tier: conferenceHotel.tier || 'A',
          invoiceNotes: conferenceHotel.invoiceNotes || ''
        });
      } else {
        // Reset form for new hotel
        setFormData({
          hotel: '',
          contactEmail: '',
          contactName: '',
          contactPhone: '',
          dateToSendCSVFile: '',
          earliestAvailable: '',
          latestAvailable: '',
          hasBusParking: false,
          hasTransitToVenue: false,
          requiresCreditCard: false,
          taxPerRoomNight: '',
          taxPerRoomNightNonProfit: '',
          taxRatePercentage: '',
          taxRatePercentageNonProfit: '',
          tier: 'A',
          invoiceNotes: ''
        });
        fetchAvailableHotels();
      }
      setError('');
    }
  }, [open, isEditMode, conferenceHotel]);

  const fetchAvailableHotels = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await fetch(
        `${APIHOST}/api/hotels?sort=longName:asc&pagination[start]=0&pagination[limit]=100`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAvailableHotels(data.data || []);
      }
    } catch (err) {
      console.error('Error fetching available hotels:', err);
    }
  };

  const handleFormChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Validate required fields
      if (!isEditMode && !formData.hotel) {
        throw new Error('Please select a hotel');
      }
      if (!formData.earliestAvailable) {
        throw new Error('Earliest Available date is required');
      }
      if (!formData.latestAvailable) {
        throw new Error('Latest Available date is required');
      }
      if (new Date(formData.latestAvailable) <= new Date(formData.earliestAvailable)) {
        throw new Error('Latest Available date must be after Earliest Available date');
      }

      const requestData = {
        data: {
          ...(isEditMode ? {} : { conference: conference.documentId, hotel: formData.hotel }),
          contactEmail: formData.contactEmail || null,
          contactName: formData.contactName || null,
          contactPhone: formData.contactPhone || null,
          dateToSendCSVFile: formData.dateToSendCSVFile || null,
          earliestAvailable: formData.earliestAvailable || null,
          latestAvailable: formData.latestAvailable || null,
          hasBusParking: formData.hasBusParking,
          hasTransitToVenue: formData.hasTransitToVenue,
          requiresCreditCard: formData.requiresCreditCard,
          taxPerRoomNight: formData.taxPerRoomNight ? parseFloat(formData.taxPerRoomNight) : null,
          taxPerRoomNightNonProfit: formData.taxPerRoomNightNonProfit ? parseFloat(formData.taxPerRoomNightNonProfit) : null,
          taxRatePercentage: formData.taxRatePercentage ? parseFloat(formData.taxRatePercentage) : null,
          taxRatePercentageNonProfit: formData.taxRatePercentageNonProfit ? parseFloat(formData.taxRatePercentageNonProfit) : null,
          tier: formData.tier,
          invoiceNotes: formData.invoiceNotes || null
        }
      };

      const method = isEditMode ? 'PUT' : 'POST';
      const url = isEditMode 
        ? `${APIHOST}/api/conference-hotels/${conferenceHotel?.documentId}`
        : `${APIHOST}/api/conference-hotels`;

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `Failed to ${isEditMode ? 'update' : 'create'} conference hotel`);
      }

      onUpdate();
      onClose();

    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${isEditMode ? 'update' : 'create'} conference hotel`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {isEditMode 
          ? `Edit Hotel Details - ${conferenceHotel?.hotel?.longName}`
          : `Add Hotel to ${conference.longName}`
        }
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 1 }}>
          {!isEditMode && (
            <>
              <FormControl fullWidth required sx={{ mb: 3 }}>
                <InputLabel>Select Hotel</InputLabel>
                <Select
                  value={formData.hotel}
                  onChange={(e) => handleFormChange('hotel', e.target.value)}
                  label="Select Hotel"
                >
                  {availableHotels.map((hotel) => (
                    <MenuItem key={hotel.documentId} value={hotel.documentId}>
                      {hotel.longName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Divider sx={{ mb: 3 }} />
            </>
          )}

          <Typography variant="h6" gutterBottom>
            Contact Information
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Contact Name"
                value={formData.contactName}
                onChange={(e) => handleFormChange('contactName', e.target.value)}
                fullWidth
                placeholder="Hotel contact person"
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Contact Email"
                type="email"
                value={formData.contactEmail}
                onChange={(e) => handleFormChange('contactEmail', e.target.value)}
                fullWidth
                placeholder="contact@hotel.com"
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Contact Phone"
                value={formData.contactPhone}
                onChange={(e) => handleFormChange('contactPhone', e.target.value)}
                fullWidth
                placeholder="(555) 123-4567"
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="CSV File Send Date"
                type="date"
                value={formData.dateToSendCSVFile}
                onChange={(e) => handleFormChange('dateToSendCSVFile', e.target.value)}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                helperText="When to send reservation data to hotel"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />
          
          <Typography variant="h6" gutterBottom>
            Availability Dates
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Earliest Available Date"
                type="date"
                value={formData.earliestAvailable}
                onChange={(e) => handleFormChange('earliestAvailable', e.target.value)}
                fullWidth
                required
                InputLabelProps={{
                  shrink: true,
                }}
                helperText="First date hotel rooms are available"
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Latest Available Date"
                type="date"
                value={formData.latestAvailable}
                onChange={(e) => handleFormChange('latestAvailable', e.target.value)}
                fullWidth
                required
                error={!!(formData.earliestAvailable && formData.latestAvailable && new Date(formData.latestAvailable) <= new Date(formData.earliestAvailable))}
                InputLabelProps={{
                  shrink: true,
                }}
                helperText={formData.earliestAvailable && formData.latestAvailable && new Date(formData.latestAvailable) <= new Date(formData.earliestAvailable) ? "Must be after Earliest Available date" : "Last date hotel rooms are available"}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />
          
          <Typography variant="h6" gutterBottom>
            Hotel Services
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.hasBusParking}
                    onChange={(e) => handleFormChange('hasBusParking', e.target.checked)}
                  />
                }
                label="Bus Parking Available"
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.hasTransitToVenue}
                    onChange={(e) => handleFormChange('hasTransitToVenue', e.target.checked)}
                  />
                }
                label="Transit to Venue"
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.requiresCreditCard}
                    onChange={(e) => handleFormChange('requiresCreditCard', e.target.checked)}
                  />
                }
                label="Credit Card Required"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />
          
          <Typography variant="h6" gutterBottom>
            Tax Configuration
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Tax Per Room/Night (Regular)"
                type="number"
                value={formData.taxPerRoomNight}
                onChange={(e) => handleFormChange('taxPerRoomNight', e.target.value)}
                fullWidth
                inputProps={{ min: 0, step: 0.01 }}
                placeholder="0.00"
                helperText="Fixed tax amount per room per night"
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Tax Per Room/Night (Non-Profit)"
                type="number"
                value={formData.taxPerRoomNightNonProfit}
                onChange={(e) => handleFormChange('taxPerRoomNightNonProfit', e.target.value)}
                fullWidth
                inputProps={{ min: 0, step: 0.01 }}
                placeholder="0.00"
                helperText="Fixed tax for non-profit organizations"
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Tax Rate Percentage (Regular)"
                type="number"
                value={formData.taxRatePercentage}
                onChange={(e) => handleFormChange('taxRatePercentage', e.target.value)}
                fullWidth
                inputProps={{ min: 0, max: 100, step: 0.01 }}
                placeholder="0.00"
                helperText="Percentage tax rate"
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Tax Rate Percentage (Non-Profit)"
                type="number"
                value={formData.taxRatePercentageNonProfit}
                onChange={(e) => handleFormChange('taxRatePercentageNonProfit', e.target.value)}
                fullWidth
                inputProps={{ min: 0, max: 100, step: 0.01 }}
                placeholder="0.00"
                helperText="Percentage tax rate for non-profit"
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Invoice Notes"
                value={formData.invoiceNotes}
                onChange={(e) => handleFormChange('invoiceNotes', e.target.value)}
                fullWidth
                multiline
                rows={3}
                placeholder="Additional notes for invoicing or billing..."
                helperText="Special instructions or notes that will be added to invoices related to this hotel"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />
          
          <Typography variant="h6" gutterBottom>
            Hotel Tier
          </Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={formData.tier === 'B'}
                onChange={(e) => handleFormChange('tier', e.target.checked ? 'B' : 'A')}
                color="primary"
              />
            }
            label={formData.tier === 'B' ? "Tier B" : "Tier A"}
            labelPlacement="start"
          />
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={!!(loading || (!isEditMode && !formData.hotel) || !formData.earliestAvailable || !formData.latestAvailable || (formData.earliestAvailable && formData.latestAvailable && new Date(formData.latestAvailable) <= new Date(formData.earliestAvailable)))}
        >
          {loading ? <CircularProgress size={20} /> : (isEditMode ? 'Update Hotel' : 'Add Hotel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}